"use client";

import { Suspense, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Dropdown, Space, Tag, Tooltip, message } from 'antd';
import { EyeOutlined, InfoCircleOutlined, MoreOutlined, SettingOutlined } from '@ant-design/icons';
import { ElectronicContractModel } from '@/core/models/electronic-contract.model';
import { ElectronicContractRepo } from '@/core/repos/tables/electronic-contract.repo';
import AuthRepo from '@/core/repos/auth.repo';
import { PERMISSIONS } from '@/core/enums/permissions.enum';
import { documentTypeLabels } from '@/core/utils/electronic-contract';
import container from '@/lib/di/container';
import { useTitleDataStore } from '@/core/stores/title-data.store';
import { formatDateTimePipe } from '@/core/pipes/format-date.pipe';
import { FilterInputTemplate, FilterInputType, FilterBar } from '@/app/home/components/filter/FilterBar';
import { DataTable, FilterSearchPayload } from '@/app/home/components/table/DataTable';
import { DeleteButton } from '@/app/home/components/table/DeleteButton';
import { CreateElectronicContractMultiModal } from './modals/CreateElectronicContractMultiModal';
import { ViewDocumentModal } from './modals/ViewDocumentModal';
import { ContractLifecycleModal } from './modals/ContractLifecycleModal';
import { TemplateConfigurationModal } from './modals/TemplateConfigurationModal';

const repo = container.get(ElectronicContractRepo);
const authRepo = container.get(AuthRepo);
const statusLabels: Record<string, string> = {
    Creating: 'Đang tạo, chờ đối soát', CreateUncertain: 'Chưa xác định kết quả tạo', Failed: 'Tạo thất bại',
    Created: 'Đã tạo, chờ ký', Sent: 'Chờ người ký', Completed: 'Hoàn tất', Cancelled: 'Đã hủy',
    Signed: 'Đã ký (hồ sơ cũ)', Pending: 'Chờ ký', Draft: 'Nháp',
};
const filters: FilterInputTemplate[] = [
    { dataIndex: 'search', label: 'Tìm kiếm', placeholder: 'Tên, mã khách hàng hoặc chứng từ', type: FilterInputType.text },
    { dataIndex: 'waterUserCode', label: 'Mã khách hàng', placeholder: 'Nhập mã khách hàng', type: FilterInputType.text },
    { dataIndex: 'status', label: 'Trạng thái', placeholder: 'Chọn trạng thái', type: FilterInputType.select,
        options: Object.entries(statusLabels).map(([value, name]) => ({ name, value })) },
];

function ElectronicContract() {
    const setTitleActions = useTitleDataStore(state => state.setTitleActions);
    const claims = authRepo.useClaims();
    const user = authRepo.useDetails();
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(new Map());
    const [selectedRows, setSelectedRows] = useState<ElectronicContractModel[]>([]);
    const [record, setRecord] = useState<ElectronicContractModel | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [configurationOpen, setConfigurationOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const admin = user?.userName?.toLowerCase() === 'admin' || claims.some(claim => claim.id === PERMISSIONS.ADMIN.key);
    const canSign = admin || claims.some(claim => claim.id === PERMISSIONS.ELECTRONICCONTRACT_SIGN.key);
    const refresh = useCallback(() => { repo.refresh(); }, []);
    const environment = process.env.REACT_APP_ECONTRACT_ENVIRONMENT;
    useEffect(() => {
        setTitleActions({ text: 'Tạo hồ sơ điện tử', onPress: () => setCreateOpen(true) });
        return () => setTitleActions({});
    }, [setTitleActions]);
    const deleteSelected = async () => {
        setDeleting(true);
        try {
            const result = await repo.deleteMany(selectedRows);
            const content = `Đã xóa ${result.success}/${result.total} hồ sơ. Hồ sơ đã phát hành phải được xử lý theo trạng thái tại nhà cung cấp.`;
            if (result.success === result.total) messageApi.success(content); else messageApi.warning(content);
            setSelectedRows([]);
        } finally { setDeleting(false); }
    };
    return <div className="px-4 py-6">
        {contextHolder}
        <Space wrap className="mb-4 w-full justify-between">
            <p className="text-gray-600">Hồ sơ điện tử · 6 loại chứng từ · Cá nhân và doanh nghiệp</p>
            <Space wrap>
                {environment && <Tag color={environment === 'production' ? 'green' : 'orange'}>{environment === 'production' ? 'Production' : 'UAT / demo'}</Tag>}
                <Button icon={<SettingOutlined />} onClick={() => setConfigurationOpen(true)}>Cấu hình mẫu</Button>
            </Space>
        </Space>
        <Alert type="info" showIcon className="mb-4" message="Tạo hồ sơ sau khi xem trước dữ liệu; theo dõi từng bên ký và đồng bộ đến khi hoàn tất."
            description="Khách hàng nhận thư mời ký từ Mắt Bão. Công ty ký Server trong chi tiết hồ sơ khi đã đến lượt. Mẫu nghiệm thu lắp đặt số 02 có giới hạn riêng theo môi trường." />
        <FilterBar setMap={setFilterMap} onChange={async map => setFilterMap(new Map(map))} templates={filters} map={filterMap}
            actions={[<div key="delete" className="col-span-4"><DeleteButton loading={deleting} length={selectedRows.length} onConfirm={deleteSelected} /></div>]} />
        <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
            <DataTable<ElectronicContractModel> repository={repo} filterMap={filterMap} onSelect={setSelectedRows} columns={[
                { dataIndex: 'waterUserCode', name: 'Mã khách hàng' },
                { dataIndex: 'customerName', name: 'Tên khách hàng' },
                { dataIndex: 'documentType', name: 'Loại hồ sơ', render: (_, item) => documentTypeLabels[item.documentType || ''] || item.documentType || 'Hồ sơ cũ' },
                { dataIndex: 'customerType', name: 'Đối tượng', render: (_, item) => item.customerType === 'DN' ? 'Doanh nghiệp' : item.customerType === 'CN' ? 'Cá nhân' : '—' },
                { dataIndex: 'documentCode', name: 'Mã chứng từ' },
                { dataIndex: 'status', name: 'Trạng thái', render: (_, item) => <Tooltip title={item.errorMessage}>
                    <Tag color={item.status === 'Completed' && item.providerStatus === 'DONE' ? 'success' : ['Failed', 'CreateUncertain'].includes(item.status || '') ? 'error' : 'default'}>
                        {item.status === 'Completed' && item.providerStatus !== 'DONE' ? 'Cần đối soát' : statusLabels[item.status || ''] || item.status || 'Chưa xác định'}
                    </Tag></Tooltip> },
                { dataIndex: 'providerStatus', name: 'Mắt Bão' },
                { dataIndex: 'lastSyncedAt', name: 'Đồng bộ lúc', pipe: formatDateTimePipe },
                { dataIndex: 'signedAt', name: 'Ngày ký', pipe: formatDateTimePipe },
                { dataIndex: 'createdAt', name: 'Ngày tạo', pipe: formatDateTimePipe },
                { name: 'Thao tác', fixed: 'right', render: (_, item) => <Dropdown trigger={['click']} menu={{ items: [
                    { key: 'view', label: 'Xem PDF', icon: <EyeOutlined />, onClick: () => { setRecord(item); setViewOpen(true); } },
                    ...(item.documentType ? [{ key: 'detail', label: 'Chi tiết, đồng bộ và ký', icon: <InfoCircleOutlined />, onClick: () => { setRecord(item); setDetailOpen(true); } }] : []),
                ] }}><Button aria-label="Thao tác hợp đồng" type="text" icon={<MoreOutlined />} /></Dropdown> },
            ]} />
        </div>
        <CreateElectronicContractMultiModal isModalOpen={createOpen} setIsModalOpen={setCreateOpen} query={{}} />
        <TemplateConfigurationModal isModalOpen={configurationOpen} setIsModalOpen={setConfigurationOpen} onSuccess={refresh} />
        {record && <>
            <ViewDocumentModal isModalOpen={viewOpen} setIsModalOpen={setViewOpen} contractId={record.id} isV2={!!record.documentType} />
            <ContractLifecycleModal isModalOpen={detailOpen} setIsModalOpen={setDetailOpen} data={record} canSign={canSign}
                scope={`${process.env.REACT_APP_API_URL}:${user?.waterFactoryId || ''}:${user?.id || ''}`} onSuccess={refresh} />
        </>}
    </div>;
}
export default function ElectronicContractPage() {
    return <Suspense><ElectronicContract /></Suspense>;
}
